import { map } from 'rxjs/operators';
import { AtsObject } from '../classes';
// Thanks to https://dev.to/codephobia/using-typescript-to-sort-by-keys-35ob

// Errors.
const ERROR_REQUIRES_AT_LEAST_ONE_KEY = 'Provide at least one key to sort by';
const ERROR_KEY_LENGTH_INVALID = 'A key was provided as an empty string';
const ERROR_DESC_KEY_LENGTH_INVALID = 'A descending key was missing the key name';
const ERROR_OBJECT_DOESNT_CONTAIN_KEY = 'A key you are attempting to sort by is not on all objects';

/**
 *  Recursive function to sort values by their keys.
 */
const _atsSortFn = <T>(a: T, b: T, ...keys: string[]): number => {
	// Get first key in array.
	let keyVal = keys.shift()!;

	// Make sure we have a valid key name.
	if (!keyVal.length) {
		throw new Error(ERROR_KEY_LENGTH_INVALID);
	}

	// Default to ascending order.
	let desc = false;

	// Check for descending sort.
	if (keyVal.charAt(0) === '-') {
		// Make sure key has a name as well as the minus sign.
		if (keyVal.length < 2) {
			throw new Error(ERROR_DESC_KEY_LENGTH_INVALID);
		}

		// Remove minus from key name.
		keyVal = keyVal.substr(1);

		// Flag as descending order.
		desc = true;
	}

	const key = keyVal.includes('.') ? [...keyVal.split('.')] : [keyVal];
	let aVal: any;
	let bVal: any;

	// Make sure the objects both have the key. We make sure
	// to check this after we have removed the minus sign.
	if (key.length > 1) {
		if (!AtsObject.hasProperties(a, key) || !AtsObject.hasProperties(b, key)) {
			throw new Error(ERROR_OBJECT_DOESNT_CONTAIN_KEY + ': ' + key);
		}

		aVal = AtsObject.deepValue(a, key);
		bVal = AtsObject.deepValue(b, key);
	} else {
		if (!Object.prototype.hasOwnProperty.call(a, key[0]) || !Object.prototype.hasOwnProperty.call(b, key[0])) {
			throw new Error(ERROR_OBJECT_DOESNT_CONTAIN_KEY + ': ' + key);
		}

		aVal = (a as Record<string, any>)[key[0]];
		bVal = (b as Record<string, any>)[key[0]];
	}

	// Determine checks based on asc / desc.
	const direction = desc ? -1 : 1;

	// If they are the same, sort by next available key
	if (aVal === bVal && keys.length) return _atsSortFn(a, b, ...keys);

	// Handle the nulls
	if ([null, 'NA', 'N/A'].includes(aVal)) {
		return 1 * direction;
	}
	if ([null, 'NA', 'N/A'].includes(bVal)) {
		return -1 * direction;
	}

	// Handle strings
	if (typeof aVal === 'string' && typeof bVal === 'string') {
		// MDN says Intl.Collator is faster
		// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare#performance
		return Intl.Collator(undefined, { sensitivity: 'base' }).compare(aVal, bVal) * direction;
	}

	// Perform bubble sort on numbers
	if (aVal > bVal) {
		return 1 * direction;
	}
	if (aVal < bVal) {
		return -1 * direction;
	}

	// Catchall and there are more keys
	if (keys.length) {
		return _atsSortFn(a, b, ...keys);
	}

	// All keys returned and no more sorting needed.
	return 0;
};

/**
 * Wrapper sort function for the recursive one.
 */
export const atsSortFn = <T>(data: T[], ...keys: string[]): T[] => {
	// Make sure we have at least one key to sort by.
	if (!keys.length) {
		throw new Error(ERROR_REQUIRES_AT_LEAST_ONE_KEY);
	}

	if (!data || !data.length) return [];

	// Sort data.
	data = data.slice().sort((a: T, b: T): number => {
		return _atsSortFn(a, b, ...keys);
	});

	// Return sorted data.
	return data;
};

export const atsSort = <T>(...keys: string[]) => map((x: T[]): T[] => atsSortFn(x, ...keys));

export const atsPrimitiveSortFn = ([...data]: (number | string)[]): (number | string)[] => {
	return data.sort((a: any, b: any) => {
		if (['', null, 'NA', 'N/A'].includes(a)) return 1;
		if (['', null, 'NA', 'N/A'].includes(b)) return -1;
		if (isNaN(a) && isNaN(b)) {
			// MDN says Intl.Collator is faster
			// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/localeCompare#performance
			return Intl.Collator(undefined, { sensitivity: 'base' }).compare(a, b);
		} else {
			return Number(a) - Number(b);
		}
	});
};

export const atsPrimitiveSort = <T>([...data]: (number | string)[]) =>
	map((x: (number | string)[]): (number | string)[] => atsPrimitiveSortFn(x));
